import React, { useState, useEffect } from "react";
import { UpdatePreferencesBody } from "target_wasm";
import {
  fetchNotificationPreferences,
  setNotificationPreferences,
} from "./NotificationsApi";
import { createLogger } from "debug/DebugingLogger";
import { NotificationSettingItems } from "./NotificationSettingItems";
import { NotificationSettingsActions } from "./NotificationSettingsActions";
import { NotificationSettingsError } from "./NotificationSettingsError";
import { showNotificationGlobal } from "stores/useNotificationStore";

const log = createLogger("NotificationSettings");

interface NotificationSettingsProps {
  clientId: string;
  initialPreferences?: PreferenceState;
}

interface PreferenceState {
  inApp: boolean;
  email: boolean;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  clientId,
  initialPreferences,
}) => {
  const [preferences, setPreferences] = useState<PreferenceState | null>(
    initialPreferences ?? null,
  );
  const [originalPreferences, setOriginalPreferences] =
    useState<PreferenceState | null>(initialPreferences ?? null);
  const [loading, setLoading] = useState(!initialPreferences);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolvePreferences = async () => {
      try {
        setLoading(true);
        if (initialPreferences) {
          setPreferences(initialPreferences);
          setOriginalPreferences(initialPreferences);
          setError(null);
          return;
        }
        const prefs = await fetchNotificationPreferences();
        const newPrefs = {
          inApp: prefs.enabled,
          email: prefs.in_email_notification,
        };
        setPreferences(newPrefs);
        setOriginalPreferences(newPrefs);
        setError(null);
      } catch (err) {
        log.error("Failed to load preferences:", err);
        setError("Failed to load preferences");
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      resolvePreferences();
    }
  }, [clientId, initialPreferences]);

  const handlePreferenceChange = (type: "inApp" | "email", value: boolean) => {
    if (preferences) {
      const newPreferences = {
        ...preferences,
        [type]: value,
      };
      setPreferences(newPreferences);
    }
  };

  const handleSave = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      setError(null);

      const wasmPrefs = UpdatePreferencesBody.new_js(
        preferences.email,
        preferences.inApp,
      );

      await setNotificationPreferences(wasmPrefs);
      setOriginalPreferences(preferences);
      log.info("Preferences updated successfully");
      showNotificationGlobal("Notification preferences saved", "default");
    } catch (err) {
      log.error("Failed to update preferences:", err);
      setError("Failed to update preferences");
      showNotificationGlobal(
        "Failed to save notification preferences",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalPreferences) {
      setPreferences(originalPreferences);
    }
    setError(null);
  };

  const hasChanges =
    preferences && originalPreferences
      ? JSON.stringify(preferences) !== JSON.stringify(originalPreferences)
      : false;

  if (loading || !preferences) {
    return (
      <div className="space-y-4">
        <NotificationSettingsError error={error} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NotificationSettingsError error={error} />
      <NotificationSettingItems
        preferences={preferences}
        onPreferenceChange={handlePreferenceChange}
      />
      <NotificationSettingsActions
        hasChanges={hasChanges}
        saving={saving}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </div>
  );
};
